import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, MessageSquarePlus, Trash2, Copy, Check, Bot } from "lucide-react";
import { VISIBILITIES, optOf, labelOf, TONE } from "@/config/commOptions";
import { toast } from "sonner";

const VisibilityPill = ({ value }) => {
  const o = optOf(VISIBILITIES, value || "internal");
  const t = TONE[o?.tone || "gray"];
  return (
    <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold border"
      style={{ backgroundColor: t.bg, color: t.color, borderColor: t.border }} data-testid="note-visibility-badge">
      {o?.label || labelOf(VISIBILITIES, value)}
    </span>
  );
};

export default function NotesPanel({ relatedType, relatedId, branchId }) {
  const { user } = useAuth();
  const canWrite = !user?.is_portal;
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [adding, setAdding] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState("");
  const [aiTitle, setAiTitle] = useState("");
  const [copied, setCopied] = useState(false);
  const [visibility, setVisibility] = useState("internal");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notes", { params: { related_type: relatedType, related_id: relatedId } });
      setNotes(data.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (relatedId) load(); /* eslint-disable-next-line */ }, [relatedId]);

  const addNote = async (text, aiGenerated = false) => {
    if (!text?.trim()) return;
    setAdding(true);
    try {
      await api.post("/notes", { related_type: relatedType, related_id: relatedId, body: text, ai_generated: aiGenerated, visibility, branch_id: branchId });
      setBody("");
      await load();
    } catch (e) {
      toast.error("Could not add note");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/notes/${id}`);
    load();
  };

  const runAI = async (kind) => {
    setAiTitle(kind === "summarize" ? "AI Summary" : "AI Draft Follow-up");
    setAiResult("");
    setAiOpen(true);
    setAiLoading(true);
    try {
      const ep = kind === "summarize" ? "/ai/summarize" : "/ai/draft-followup";
      const { data } = await api.post(ep, { related_type: relatedType, related_id: relatedId });
      setAiResult(data.result || "");
    } catch (e) {
      const msg = e?.response?.data?.detail || "AI assist failed";
      setAiResult("");
      toast.error(msg);
      setAiOpen(false);
    } finally {
      setAiLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(aiResult);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-4">
      {canWrite && (
        <div className="rounded-xl border border-[#E6EDF2] p-3 bg-white">
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2} placeholder="Add a note..." data-testid="note-input" />
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <Button size="sm" onClick={() => addNote(body)} disabled={adding || !body.trim()} data-testid="add-note-button"
              className="text-white" style={{ backgroundColor: "var(--mmm-navy)" }}>
              <MessageSquarePlus size={15} className="mr-1.5" /> Add note
            </Button>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="note-visibility-select"><SelectValue /></SelectTrigger>
              <SelectContent>{VISIBILITIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={() => runAI("summarize")} data-testid="ai-summarize-notes-button">
                <Sparkles size={15} className="mr-1.5 text-[#9C1E22]" /> Summarize
              </Button>
              <Button size="sm" variant="outline" onClick={() => runAI("draft")} data-testid="ai-draft-follow-up-button">
                <Bot size={15} className="mr-1.5 text-[#9C1E22]" /> Draft follow-up
              </Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-2"><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /></div>
      ) : notes.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">No notes yet. Add the first note or use AI to summarize.</p>
      ) : (
        <div className="space-y-2.5">
          {notes.map((n) => (
            <div key={n.id} className="rounded-xl border border-[#E6EDF2] p-3 bg-white" data-testid="note-item">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold text-[#1C4358]">{n.author_name || "Team"}</span>
                <VisibilityPill value={n.visibility} />
                {n.ai_generated && (
                  <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "rgba(156,30,34,0.10)", color: "#9C1E22" }}>
                    <Sparkles size={10} /> AI
                  </span>
                )}
                <span className="text-[11px] text-muted-foreground ml-auto">{formatDateTime(n.created_at)}</span>
                {canWrite && (
                  <button onClick={() => remove(n.id)} className="text-muted-foreground hover:text-[#9C1E22]" data-testid="delete-note-button">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
              <p className="text-sm text-[#252221] whitespace-pre-wrap leading-relaxed">{n.body}</p>
            </div>
          ))}
        </div>
      )}

      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-lg" data-testid="ai-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles size={18} className="text-[#9C1E22]" /> {aiTitle}</DialogTitle>
          </DialogHeader>
          {aiLoading ? (
            <div className="space-y-2 py-2">
              <Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-11/12" /><Skeleton className="h-4 w-10/12" /><Skeleton className="h-4 w-9/12" />
            </div>
          ) : (
            <div className="rounded-xl bg-[#F7FAFC] border border-[#E6EDF2] p-3 max-h-[50vh] overflow-y-auto mmm-scroll">
              <p className="text-sm whitespace-pre-wrap leading-relaxed text-[#252221]">{aiResult}</p>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={copy} disabled={!aiResult} data-testid="ai-copy-button">
              {copied ? <Check size={15} className="mr-1.5" /> : <Copy size={15} className="mr-1.5" />} {copied ? "Copied" : "Copy"}
            </Button>
            <Button onClick={() => { addNote(aiResult, true); setAiOpen(false); }} disabled={!aiResult || !canWrite}
              data-testid="ai-save-note-button" className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}>
              Save as note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
